import React, {
  useContext,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { LanguageContext } from "../../contexts/LanguageContext/LanguageContext";
import usePlatformConfig from "../../utils/UsePlatformConfig";

const LanguageSelection = forwardRef(
  ({ configPath, onLanguageSelectionChanged, localStorageKey }, ref) => {
    const {
      languages,
      loading: languagesLoading,
      error: languagesError,
    } = useContext(LanguageContext);

    // Use platform config hook for fetching and mobile detection
    const {
      config,
      error: configError,
      isMobile,
    } = usePlatformConfig(configPath);

    const [visibleLanguages, setVisibleLanguages] = useState([]);
    const [remainingLanguages, setRemainingLanguages] = useState([]);
    const [showMore, setShowMore] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [pressedLanguage, setPressedLanguage] = useState(null);
    const [filterText, setFilterText] = useState("");

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const selectedStorageKey = `${localStorageKey}_selected`;

    const inputRef = useRef(null);

    useEffect(() => {
      console.log("Component mounted, localStorageKey:", localStorageKey);
      console.log("Selected storage key:", selectedStorageKey);
      console.log("Is mobile:", isMobile);
    }, [localStorageKey, selectedStorageKey, isMobile]);

    useEffect(() => {
      if (languages && config && !languagesLoading) {
        const sortedLanguages = [...languages].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        console.log(
          "Sorted languages:",
          sortedLanguages.map((l) => l.name)
        );

        const { visibleCount, initiallyVisible } = config;

        // Load visible languages
        const storedLanguages = JSON.parse(
          localStorage.getItem(localStorageKey)
        );
        console.log("Stored visible languages:", storedLanguages);
        let initialVisible;

        if (storedLanguages && storedLanguages.length > 0) {
          initialVisible = storedLanguages
            .map((name) => sortedLanguages.find((lang) => lang.name === name))
            .filter(Boolean);
        } else {
          initialVisible = initiallyVisible
            .map((name) => sortedLanguages.find((lang) => lang.name === name))
            .filter(Boolean);
        }

        let newVisibleLanguages;
        if (initialVisible.length >= visibleCount) {
          newVisibleLanguages = initialVisible.slice(0, visibleCount);
        } else {
          const remainingSorted = sortedLanguages.filter(
            (lang) => !initialVisible.some((iv) => iv.id === lang.id)
          );
          newVisibleLanguages = [
            ...initialVisible,
            ...remainingSorted.slice(0, visibleCount - initialVisible.length),
          ];
        }

        const newRemainingLanguages = sortedLanguages.filter(
          (lang) => !newVisibleLanguages.some((vl) => vl.id === lang.id)
        );

        console.log(
          "Setting visible languages:",
          newVisibleLanguages.map((l) => l.name)
        );
        setVisibleLanguages(newVisibleLanguages);
        setRemainingLanguages(newRemainingLanguages);

        // Load selected language
        const storedSelected = JSON.parse(
          localStorage.getItem(selectedStorageKey)
        );
        console.log("Stored selected language:", storedSelected);
        if (storedSelected && storedSelected.id) {
          const selected = sortedLanguages.find(
            (lang) => lang.id === storedSelected.id
          );
          if (selected) {
            console.log("Restoring selected language:", selected);
            setSelectedLanguage(selected);
            if (newVisibleLanguages.some((vl) => vl.id === selected.id)) {
              setVisibleLanguages([
                selected,
                ...newVisibleLanguages.filter((vl) => vl.id !== selected.id),
              ]);
              setRemainingLanguages(
                newRemainingLanguages.filter((rl) => rl.id !== selected.id)
              );
            } else {
              const displacedLanguage = newVisibleLanguages[0];
              setVisibleLanguages([
                selected,
                ...newVisibleLanguages.slice(1, visibleCount),
              ]);
              setRemainingLanguages(
                [
                  ...newRemainingLanguages.filter(
                    (rl) => rl.id !== selected.id
                  ),
                  displacedLanguage,
                ].sort((a, b) => a.name.localeCompare(b.name))
              );
            }
          } else {
            console.log("Selected language not found in available languages");
            localStorage.setItem(selectedStorageKey, JSON.stringify(null));
          }
        } else {
          console.log("No valid selected language in storage");
          localStorage.setItem(selectedStorageKey, JSON.stringify(null));
        }
      }
    }, [
      languages,
      config,
      languagesLoading,
      localStorageKey,
      selectedStorageKey,
    ]);

    useEffect(() => {
      if (visibleLanguages.length > 0) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify(visibleLanguages.map((lang) => lang.name))
        );
        console.log(
          "Saved visible languages:",
          visibleLanguages.map((l) => l.name)
        );
      }
    }, [visibleLanguages, localStorageKey]);

    useEffect(() => {
      if (showMore) {
        inputRef.current?.focus();
      } else {
        setFilterText("");
      }
    }, [showMore]);

    useImperativeHandle(ref, () => ({
      getSelectedLanguage: () => selectedLanguage,
      setLanguage: (language) => {
        console.log("Setting language programmatically:", language);
        setSelectedLanguage(language);
        localStorage.setItem(selectedStorageKey, JSON.stringify(language));
        if (language && language.id && config) {
          const isVisible = visibleLanguages.some(
            (vl) => vl.id === language.id
          );
          let newVisibleLanguages;
          let newRemainingLanguages = [...remainingLanguages];

          if (isVisible) {
            newVisibleLanguages = [
              language,
              ...visibleLanguages.filter((vl) => vl.id !== language.id),
            ].slice(0, config.visibleCount);
          } else {
            const displacedLanguage = visibleLanguages[0];
            newVisibleLanguages = [
              language,
              ...visibleLanguages.slice(1, config.visibleCount),
            ];
            newRemainingLanguages = [
              ...remainingLanguages.filter((rl) => rl.id !== language.id),
              displacedLanguage,
            ].sort((a, b) => a.name.localeCompare(b.name));
          }

          setVisibleLanguages(newVisibleLanguages);
          setRemainingLanguages(
            newRemainingLanguages.filter(
              (rl) => !newVisibleLanguages.some((vl) => vl.id === rl.id)
            )
          );
          console.log(
            "Updated visible languages with selected first:",
            newVisibleLanguages.map((l) => l.name)
          );
        }
      },
    }));

    const handleMoreClick = () => {
      setShowMore(!showMore);
    };

    const handleLanguageSelect = (language) => {
      console.log("Clicked language:", language);

      if (selectedLanguage?.id === language.id) {
        console.log("Deselecting language:", language);
        setSelectedLanguage(null);
        localStorage.setItem(selectedStorageKey, JSON.stringify(null));
        setPressedLanguage(null);
        setShowMore(false);
        setFilterText(""); // Reset filter on deselect

        if (
          onLanguageSelectionChanged &&
          typeof onLanguageSelectionChanged === "function"
        ) {
          onLanguageSelectionChanged(null);
        }
        return;
      }

      console.log("Selecting language:", language);
      const isVisible = visibleLanguages.some((l) => l.id === language.id);
      let newVisibleLanguages;
      let newRemainingLanguages = [...remainingLanguages];

      if (isVisible) {
        newVisibleLanguages = [
          language,
          ...visibleLanguages.filter((l) => l.id !== language.id),
        ].slice(0, config.visibleCount);
      } else {
        const displacedLanguage = visibleLanguages[0];
        newVisibleLanguages = [
          language,
          ...visibleLanguages.slice(1, config.visibleCount),
        ];
        newRemainingLanguages = [
          ...remainingLanguages.filter((l) => l.id !== language.id),
          displacedLanguage,
        ].sort((a, b) => a.name.localeCompare(b.name));
      }

      setVisibleLanguages(newVisibleLanguages);
      setRemainingLanguages(
        newRemainingLanguages.filter(
          (lang) => !newVisibleLanguages.some((vl) => vl.id === lang.id)
        )
      );
      setSelectedLanguage(language);
      localStorage.setItem(selectedStorageKey, JSON.stringify(language));
      console.log("Saved selected language in handleLanguageSelect:", language);

      setShowMore(false);
      setPressedLanguage(null);
      setFilterText(""); // Reset filter on select

      if (
        onLanguageSelectionChanged &&
        typeof onLanguageSelectionChanged === "function"
      ) {
        onLanguageSelectionChanged(language);
      }
    };

    const handleMouseDown = (language) => {
      setPressedLanguage(language);
    };

    const handleMouseUp = () => {
      setPressedLanguage(null);
    };

    const handleTouchStart = (language) => {
      setPressedLanguage(language);
    };

    const handleTouchEnd = () => {
      setPressedLanguage(null);
    };

    const handleFilterChange = (e) => {
      setFilterText(e.target.value);
    };

    if (languagesLoading) {
      return <div className="text-gray-500">Loading languages...</div>;
    }

    if (languagesError) {
      return (
        <div className="text-red-500">
          Error loading languages: {languagesError}
        </div>
      );
    }

    if (configError) {
      return (
        <div className="text-red-500">
          Error loading configuration: {configError}
        </div>
      );
    }

    if (!languages || languages.length === 0) {
      return <div className="text-gray-500">No languages available.</div>;
    }

    if (!config) {
      return <div className="text-gray-500">Loading configuration...</div>;
    }

    const { columnCount, gridWidth, gridHeight, visibleCount } = config;

    const filteredRemainingLanguages = filterText
      ? remainingLanguages.filter((language) =>
          language.name.toLowerCase().startsWith(filterText.toLowerCase())
        )
      : remainingLanguages;

    return (
      <div className="language-selection max-w-5xl mx-auto p-5">
        <div
          className="language-grid grid gap-2 justify-center"
          style={{
            gridTemplateColumns: `repeat(${visibleCount + 1}, ${gridWidth}px)`,
          }}
        >
          {visibleLanguages.map((language) => (
            <div
              key={language.id || language.code}
              className={`language-cell flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-200 cursor-pointer
                ${
                  selectedLanguage?.id === language.id
                    ? "border-blue-500 shadow-md"
                    : "border-gray-300"
                }
                ${
                  pressedLanguage?.id === language.id
                    ? "shadow-inner bg-gray-100"
                    : "shadow-sm hover:shadow-md bg-white"
                }`}
              style={{
                width: `${gridWidth}px`,
                height: `${gridHeight}px`,
              }}
              onMouseDown={() => handleMouseDown(language)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={() => handleTouchStart(language)}
              onTouchEnd={handleTouchEnd}
              onClick={() => handleLanguageSelect(language)}
            >
              <img
                src={language.flag}
                alt={`${language.name} flag`}
                className="w-3/5 h-3/5 object-contain"
                onError={(e) => {
                  e.target.src = `${SERVER_URL}/media/flags/fallback.png`;
                }}
              />
              <span className="text-sm text-center mt-1">{language.name}</span>
            </div>
          ))}
          {remainingLanguages.length > 0 && (
            <button
              className={`more-button flex items-center justify-center rounded-lg border-2 border-gray-300 transition-all duration-200
                ${
                  pressedLanguage === "more"
                    ? "shadow-inner bg-gray-100"
                    : "shadow-sm hover:shadow-md bg-white"
                }`}
              style={{
                width: `${gridWidth}px`,
                height: `${gridHeight}px`,
              }}
              onMouseDown={() => setPressedLanguage("more")}
              onMouseUp={() => setPressedLanguage(null)}
              onMouseLeave={() => setPressedLanguage(null)}
              onTouchStart={() => setPressedLanguage("more")}
              onTouchEnd={() => setPressedLanguage(null)}
              onClick={handleMoreClick}
            >
              <span className="text-sm">More</span>
            </button>
          )}
        </div>

        {showMore && remainingLanguages.length > 0 && (
          <div className="remaining-languages-container mt-5 border-t border-gray-200 pt-5">
            <div className="filter-input mb-4 max-w-md mx-auto">
              <input
                ref={inputRef}
                type="text"
                value={filterText}
                onChange={handleFilterChange}
                placeholder="Filter languages..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>
            <div
              className="remaining-languages-grid grid gap-2 justify-center"
              style={{
                gridTemplateColumns: `repeat(${columnCount}, ${gridWidth}px)`,
              }}
            >
              {filteredRemainingLanguages.length > 0 ? (
                filteredRemainingLanguages.map((language) => (
                  <div
                    key={language.id || language.code}
                    className={`language-cell flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-200 cursor-pointer
                      ${
                        pressedLanguage?.id === language.id
                          ? "shadow-inner bg-gray-100"
                          : "shadow-sm hover:shadow-md bg-white"
                      }`}
                    style={{
                      width: `${gridWidth}px`,
                      height: `${gridHeight}px`,
                    }}
                    onMouseDown={() => handleMouseDown(language)}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={() => handleTouchStart(language)}
                    onTouchEnd={handleTouchEnd}
                    onClick={() => handleLanguageSelect(language)}
                  >
                    <img
                      src={language.flag}
                      alt={`${language.name} flag`}
                      className="w-3/5 h-3/5 object-contain"
                      onError={(e) => {
                        e.target.src = `${SERVER_URL}/media/flags/fallback.png`;
                      }}
                    />
                    <span className="text-sm text-center mt-1">
                      {language.name}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm col-span-full text-center">
                  No languages match your filter.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default LanguageSelection;
